/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useState} from 'react';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {ZoomToFitIcon} from '@ion/dls/src/icons/ZoomToFitIcon';
import {mapViewGoToOptions} from '@ion/dls/src/maps/constants';
import {IconSize, MarkerProps} from '@ion/dls/src/types/types';

type HomeToolProps = Omit<MarkerProps, 'color'>;

// defines the prop definition for HomeToolFactory component...
export interface HomeToolFactoryProps {
  map: __esri.Map;
  view: __esri.MapView;
  initialExtent: __esri.Extent;
  callBack?: Function;
  layerRef?: __esri.Layer;
  isDisableInitialExtent?: boolean;
};

/**
 * The HomeTool provides the UI which displays the associated Icon button
 * within a DLS Map that, when clicked, initiates the corresponding feature.
 *
 * This HomeTool represents the "Home View" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=1526%3A7422
 *
 * ```typescript
 *
 *   <HomeTool size={} onClick={function() {}} />
 *
 * ```
 */
export const HomeTool = ({size, onClick}: HomeToolProps) => (
  <FloatingIconButtonGroup>
    <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}>
      <ZoomToFitIcon size={size} />
    </FloatingIconButtonGroup.FloatingIconButton>
  </FloatingIconButtonGroup>
);

/**
 * The HomeToolFactory component is responsible for creating a Home Tool/widget component that
 * provide an option to go to the home in the map layer. This is a stand-alone tool, can be usable
 * in any of position in the map.
 *
 * ```typescript
 *
 *  import HomeToolFactory from '../tools/HomeTool';
 *
 *  <HomeToolFactory map={map}
 *    view={view}
 *    initialExtent={initialExtent}
 *    callBack={callBack}
 *    layerRef={basemapLayerRef} />
 *
 * ```
 */
const HomeToolFactory = ({
  map,
  view,
  initialExtent,
  callBack,
  layerRef,
  isDisableInitialExtent = false
}: HomeToolFactoryProps) => {
  const [homeExtend, setHomeExtend] = useState(null);
  const {isPhone}                   = useMedia();

  useEffect(() => {
    setHomeExtend(initialExtent || view.extent);
  }, [initialExtent]);

  const homeDefaultView = function() {
    if (!isDisableInitialExtent) {
      view.goTo(homeExtend, mapViewGoToOptions);
    }
    view.popup?.close?.();
    callBack?.({map, view, layerRef});
  };

  return (
    <Tooltip title={translate('maps:controls.zoomToFit')}
      renderOnlyChildren={isPhone}
      placement='bottom'>
      <div>
        <HomeTool size={IconSize.medium} onClick={homeDefaultView} />
      </div>
    </Tooltip>
  );
};

export default memo(HomeToolFactory, (prevProps, nextProps) => (
  isEqual(prevProps.initialExtent, nextProps.initialExtent)
));
